import * as Sentry from '@sentry/browser';

interface JSONSuccessResponse {
    status: 'success';
    data: any;
}

interface JSONErrorResponse {
    status: 'error';
    error_message: string;
}

type JSONResponse = JSONSuccessResponse | JSONErrorResponse;

function tryParseJSON(body: BodyInit): any {
    try {
        const result = JSON.parse(body as any);
        return result;
    } catch (err) {
        return body;
    }
}

async function fetchJson(url: string, settings: RequestInit): Promise<any> {
    try {
        var response = await fetch(url, settings);
    } catch (err) {
        Sentry.captureException(err, {
            extra: {
                url,
                settings,
                body: tryParseJSON(settings.body),
            },
        });
        console.error(err);
        throw new Error('Network error');
    }
    if (!response.ok) {
        const text = await response.text();
        const err = new Error(`${response.status}: ${response.statusText}`);
        Sentry.captureException(err, {
            extra: {
                url,
                settings,
                body: tryParseJSON(settings.body),
            },
        });
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        console.error(`${err}\n` + text);
        throw err;
    }
    const json = await response.json() as JSONResponse;
    if (json.status !== 'success') {
        const err = new Error(json.error_message);
        Sentry.captureException(err, {
            extra: {
                url,
                settings,
                body: json,
            },
        });
        console.error(err);
        throw err;
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return json.data;
}

export async function getJson(url: string): Promise<any> {
    const settings: RequestInit = {
        credentials: 'same-origin',
        headers: {
            'X-CSRFToken': __CSRF_TOKEN__,
        },
    };
    return fetchJson(url, settings);
}

export async function postJson(url: string, data: any): Promise<any> {
    const postSettings: RequestInit = {
        credentials: 'same-origin',
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
            'X-CSRFToken': __CSRF_TOKEN__,
            'Content-Type': 'application/json',
        },
    };
    return fetchJson(url, postSettings);
}

export async function postFile(url: string, data: FormData): Promise<any> {
    data.append('_csrf_token', __CSRF_TOKEN__);
    const postSettings: RequestInit = {
        credentials: 'same-origin',
        method: 'POST',
        body: data,
        headers: {
            'X-CSRFToken': __CSRF_TOKEN__,
        },
    };
    return fetchJson(url, postSettings);
}

export async function fetchBlob(url: string, data: any): Promise<any> {
    const settings: RequestInit = {
        credentials: 'same-origin',
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
            'X-CSRFToken': __CSRF_TOKEN__,
            'Content-Type': 'application/json',
        },
    };
    try {
        var response = await fetch(url, settings);
    } catch (err) {
        Sentry.captureException(err, {
            extra: {
                url,
                settings,
                body: tryParseJSON(settings.body),
            },
        });
        console.error(err);
        throw new Error('Network error');
    }
    if (!response.ok) {
        const err = new Error(`${response.status}: ${response.statusText}`);
        Sentry.captureException(err, {
            extra: {
                url,
                settings,
                body: tryParseJSON(settings.body),
            },
        });
        console.error(err);
        throw err;
    }
    const blob = await response.blob();
    return blob;
}
